import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { formatCurrency } from '../../../../func/Function';
import { useEffect, useMemo, useState } from 'react';
import { priceAnalysisProps } from '../../data';
// import NoDataMessage from '../../components/NoData';
import { useReportContext } from '../../../../context/DetailContext';
import DetailService from '../../../../service/detail';
import LimitTable from '../../../components/limit/DetailLimitTable';

const PriceAnalysis = () => {
  const { iidx, regionAccess, quotaAccess } = useReportContext();
  const [priceAnalysisData, setPriceAnalysisData] =
    useState<priceAnalysisProps | null>(null); // 시세분석

  /** 시세분석 api */
  const GetPriceAnalysis = async (iidx: number) => {
    try {
      const res = await DetailService.priceAnalysis(iidx);
      setPriceAnalysisData(res.data);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (iidx) {
      GetPriceAnalysis(Number(iidx));
    }
  }, [iidx]);

  const chartDataSet = useMemo(() => {
    if (!priceAnalysisData) return [];
    return [
      {
        name: '감정가',
        price: Number(priceAnalysisData.appraisedPrc_meter),
      },
      {
        name: window.location.pathname.includes('/exchange/detail')
          ? '희망가'
          : '최저입찰가',
        price: Number(priceAnalysisData.minPrc_meter),
      },
      {
        name: '상위평균가',
        price: priceAnalysisData.avgPrc_high_meter,
      },
      {
        name: '중위평균가',
        price: priceAnalysisData.avgPrc_meter,
      },
      {
        name: '하위평균가',
        price: priceAnalysisData.avgPrc_low_meter,
      },
    ];
  }, [priceAnalysisData]);

  const getBarColor = (name: string) => {
    switch (name) {
      case '감정가':
        return '#0052BD';
      case '최저입찰가':
        return '#FA4032';
      case '희망가':
        return '#FA4032';
      case '중위평균가':
        return '#008AFF';
      default:
        return '#E8E9EA';
    }
  };

  const medianValue =
    chartDataSet.find((item) => item.name === '중위평균가')?.price || 0;

  function calculateElapsedYears(buildYear: string): number | null {
    if (!buildYear) return null;

    const currentYear = new Date().getFullYear();

    // 연도만 있는 경우 (예: "2001")
    if (/^\d{4}$/.test(buildYear)) {
      return currentYear - parseInt(buildYear, 10);
    }

    // 연,월,일 모두 있는 경우 (예: "20010331")
    if (/^\d{8}$/.test(buildYear)) {
      const year = parseInt(buildYear.slice(0, 4), 10);
      return currentYear - year;
    }

    // 지원하지 않는 형식일 경우
    return null;
  }

  return (
    <Container id="가격분석">
      <Title>가격분석</Title>
      {!quotaAccess || !regionAccess ? (
        <LimitTable title="가격분석은" />
      ) : (
        <Content>
          <ContentItem>
            <ContentItemTitle>
              <img src="/assets/images/icon/Icon_chart.svg" alt="" />
              시세분석
            </ContentItemTitle>
            <div style={{ width: '100%', height: '200px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartDataSet}
                  margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    tick={({ x, y, payload }) => {
                      const color =
                        payload.value === '상위평균가' ||
                        payload.value === '하위평균가'
                          ? 'rgba(23, 30, 38, 0.40)'
                          : '#171E26';

                      return (
                        <text
                          x={x}
                          y={y + 10}
                          fill={color}
                          fontSize="11px"
                          fontWeight="500"
                          fontFamily="'Spoqa Han Sans Neo', sans-serif"
                          textAnchor="middle"
                        >
                          {payload.value}
                        </text>
                      );
                    }}
                  />

                  <Bar
                    dataKey="price"
                    fill="#82ca9d"
                    barSize={40}
                    radius={4}
                    label={({ x, y, value }) => (
                      <text
                        x={x! + 20}
                        y={y! - 10}
                        fill="#333"
                        fontSize="14px"
                        fontWeight="700"
                        fontFamily="Spoqa Han Sans Neo"
                        textAnchor="middle"
                      >
                        {formatCurrency(value)}
                      </text>
                    )}
                  >
                    {chartDataSet.map((entry) => (
                      <Cell key={entry.name} fill={getBarColor(entry.name)} />
                    ))}
                  </Bar>
                  <ReferenceLine
                    y={medianValue}
                    stroke="rgba(23, 30, 38, 0.40)"
                    strokeDasharray="3 3"
                    label={{
                      position: 'insideRight',
                      value: '평균',
                      fontSize: 12,
                      fontFamily: 'Spoqa Han Sans Neo',
                      fontWeight: 500,
                      dx: 30,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ContentItem>
        </Content>
      )}
    </Container>
  );
};

export default PriceAnalysis;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 16px;
  background: white;
`;

const Title = styled.div`
  width: 100%;
  font-family: Spoqa Han Sans Neo;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 26.1px;
  letter-spacing: -0.44px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: flex-end;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ContentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ContentItemTitle = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 23.2px;
  letter-spacing: -0.36px;
  color: #171e26;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 4px;
  align-items: center;
  // div {
  //   display: flex;
  //   flex-direction: row;
  //   align-items: center;
  //   gap: 8px;
  // }
`;
